import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { useRegistration } from '@/hooks/useRegistration';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { BtnBlueLink } from '@/components/buttons/BtnBlueLink';
import { selectIsCanadaMarket, selectLocale } from '@/store/selectors/commonSelectors';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffAuthRequest, BffAuthResponse, BlockReasonType } from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { ButtonWithLoader } from '@/components/Common/ButtonWithLoader';
import { FormPassword } from '@/components/FormPassword';
import { FormInput } from '@/components/FormInput';
import PromptMessage from '@/components/UI/PromptMessage';
import { envIsMexico } from '@/constants/constants';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginFirstStep({
  onSuccessfulLogin,
  email,
}: {
  onSuccessfulLogin: (data: BffAuthResponse, payload: BffAuthRequest) => void;
  email?: string;
}): JSX.Element {
  const intl = useIntl();
  const { openModal, closeModal } = useModal();
  const { loading, error, request } = useBffApiRequest<BffAuthResponse>();
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const navigate = useAbsoluteNavigate();
  const locale = useSelector(selectLocale);
  const isCanadaMarket = useSelector(selectIsCanadaMarket);

  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.email.required' }))
      .email(intl.formatMessage({ id: 'inputs.email.invalid' })),
    password: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.password.required' }))
      .min(6, intl.formatMessage({ id: 'inputs.password.invalid' })),
  });

  const switchToSmsLogin = () => {
    triggerGTag(GTagEvents.login_modal_switch_sms);
    openModal(MODALS.LOGIN_SMS);
  };

  const submitHandler = (values: LoginForm) => {
    setErrorMessage(null);
    const payload = { username: values.email, password: values.password };

    const requestCallback = (data: BffAuthResponse) => {
      if (data.blockReason?.blockReasonType === BlockReasonType.SelfExclusion) {
        setErrorMessage(intl.formatMessage({ id: 'login.error.localSelfExclusion' }));
        return;
      }

      if (data.blockReason?.blockReasonType === BlockReasonType.SelfExclusionRegisterMatch) {
        const betGuardUrl = locale === 'ca-fr' ? 'http://garde-jeu.ca/' : 'https://betguard.ca/';
        setErrorMessage(
          <FormattedMessage
            id="login.error.centralSelfExclusion"
            values={{
              a: chunks => (
                <a href={betGuardUrl} target="_blank" rel="noopener noreferrer">
                  {chunks}
                </a>
              ),
            }}
          />,
        );
        return;
      }

      if (isCanadaMarket && data.blockReason?.blockReasonType === BlockReasonType.Timeout) {
        setErrorMessage(intl.formatMessage({ id: 'login.error.timeout' }));
        return;
      }

      onSuccessfulLogin(data, payload);
    };

    const errorCallback = (error: BffApiError) => {
      setLoginAttempts(prevState => prevState + 1);

      if (loginAttempts > 1) {
        openModal(MODALS.FAILED_LOGIN);
      }

      triggerGTag(GTagEvents.login_error, {
        errorId: error.code,
      });
    };

    request({
      apiMethod: BffApiService.login,
      payload,
      errorCallback: errorCallback,
      successCallback: requestCallback,
    });
  };

  const onForgotPasswordClicked = () => {
    closeModal(MODALS.LOGIN);
    navigate('/forgot-password');
  };

  return (
    <div>
      {(error || errorMessage) && (
        <div className="-mt-4">
          <PromptMessage
            type="error"
            message={errorMessage || intl.formatMessage({ id: 'error.1' })}
          />
        </div>
      )}
      <Formik
        initialValues={{
          email: email ?? '',
          password: '',
        }}
        validationSchema={loginSchema}
        onSubmit={values => submitHandler(values)}
      >
        {({ isValid }) => (
          <Form>
            <FormInput
              aria-label="E-mail address field"
              aria-roledescription="Field where users can input their e-mail address"
              data-cy="login-modal-email"
              name="email"
              type="email"
              label={intl.formatMessage({ id: 'inputs.email' })}
              placeholder={intl.formatMessage({
                id: 'inputs.email.placeholder',
              })}
            />
            <FormPassword
              data-cy="login-modal-password"
              aria-label="Password field"
              aria-roledescription="Password input field. Hides charcters by default, can toggle hide by clicking or navigating to icon and doing any key press."
              id="password"
              name="password"
              label={intl.formatMessage({ id: 'inputs.password' })}
              labelLink={{
                text: intl.formatMessage({ id: 'inputs.password.forgot' }),
                onClick: () => onForgotPasswordClicked(),
              }}
              placeholder={intl.formatMessage({
                id: 'inputs.password.placeholder',
              })}
            />
            <ButtonWithLoader
              loading={loading}
              message={intl.formatMessage({ id: 'login.login' })}
              disabled={!isValid || loading}
              dataCy="login-modal-submit-button"
            />
          </Form>
        )}
      </Formik>
      <div className="grid grid-cols-1 gap-4">
        {!envIsMexico() && !isRegistrationRemoved && (
          <div className="text-sm font-bold">
            {intl.formatMessage({ id: 'login.createAccountText' })}{' '}
            <button
              aria-label="Create account form change"
              type="button"
              className="cursor-pointer font-bold text-blue-medium underline transition hover:text-blue-light focus:border-[1px] focus:border-blue-light"
              onClick={() => {
                closeModal(MODALS.LOGIN);
                openRegistration({ openedFrom: OpenedFrom.LoginFirstStep });
              }}
            >
              {intl.formatMessage({ id: 'login.createAccountLink' })}
            </button>
          </div>
        )}
        {locale === 'th' && (
          <>
            <div className="h-[1px] bg-slate-400 w-full opacity-60"></div>
            <BtnBlueLink
              data-cy="login-form-switch-login-via-mobile"
              onClickHandler={switchToSmsLogin}
              text={intl.formatMessage({ id: 'authentication.login.via-mobile' })}
            />
          </>
        )}
      </div>
    </div>
  );
}
